import { CloseRoundLight, DoneLight, EyeLight, ViewHideLight } from '@repo/go-icons';
import { Button, InputNumber, Switch } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CurveColorPalette } from '../../shared/components/CurveColorPalette/CurveColorPalette';
import { MAX_POINTS_QUANTITY, MIN_POINTS_QUANTITY } from '../consts';
import styles from '../DirectionalDrillingMonitor.module.css';
import type { DirectionalDrillingState } from '../store/DirectionalDrillingState';
import type { ToolState } from '../store/ToolState';

const ToolSettings = observer(function ToolSettings({
  toolState,
  theme,
}: {
  toolState: ToolState;
  theme: 'dark' | 'light';
}) {
  const { t } = useTranslation('directionalDrillingWidget');
  const { isVisible, pointsAmount, color } = toolState.temporaryData;
  return (
    <div className={styles.curveContainer}>
      <label className={styles.visibility}>
        <Switch
          checked={isVisible}
          onChange={(value) => toolState.setTemporarySettings({ isVisible: value })}
          rightNode={<EyeLight />}
          leftNode={<ViewHideLight />}
        />
        <span>{toolState.toolName}</span>
      </label>
      <label className={styles.pointsQuantity}>
        <InputNumber
          className={styles.pointsInput}
          value={pointsAmount}
          min={MIN_POINTS_QUANTITY}
          max={MAX_POINTS_QUANTITY}
          onChange={(value) => {
            const amount = Number(value);
            if (Number.isFinite(amount)) {
              toolState.setTemporarySettings({
                pointsAmount: Math.max(MIN_POINTS_QUANTITY, Math.min(MAX_POINTS_QUANTITY, amount)),
              });
            }
          }}
        />
        <span>{t('pointsToDisplay')}</span>
      </label>
      <CurveColorPalette
        theme={theme}
        value={color}
        onChange={(value) => toolState.setTemporarySettings({ color: value })}
        getColorLabel={(number) => t('color', { number })}
      />
    </div>
  );
});

export const ParametersSettings = observer(function ParametersSettings({
  widgetState,
  close,
}: {
  widgetState: DirectionalDrillingState;
  close: () => void;
}) {
  const { t } = useTranslation('directionalDrillingWidget');
  const [is360, setIs360] = useState(widgetState.is360);
  return (
    <div className={styles.settingsContainer}>
      <div className={styles.settingsTitle}>{t('settings')}</div>
      <label className={styles.chartRegime}>
        <Switch checked={is360} onChange={setIs360} />
        <span>{t('zeroTo360')}</span>
      </label>
      <div>
        {widgetState.gravityToolState && (
          <ToolSettings toolState={widgetState.gravityToolState} theme={widgetState.appParams.theme} />
        )}
        {widgetState.magneticToolState && (
          <ToolSettings toolState={widgetState.magneticToolState} theme={widgetState.appParams.theme} />
        )}
      </div>
      <div className={styles.buttonsWrapper}>
        <Button variant="danger" icon={<CloseRoundLight />} onClick={close}>
          {t('cancel')}
        </Button>
        <Button
          variant="success"
          icon={<DoneLight />}
          onClick={() => {
            widgetState.saveSettings(is360);
            close();
          }}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
});
